import React, { cloneElement, JSX, ReactNode, useMemo } from "react";
import {
  Box,
  ClickAwayListener,
  styled,
  Tooltip as MuiTooltip,
  TooltipProps as MuiTooltipProps,
  Typography,
  useTheme,
} from "@mui/material";

const StyledTooltip = styled(({ className, ...props }: MuiTooltipProps) => (
  <MuiTooltip {...props} classes={{ popper: className }} />
))<{ variant: "default" | "white"; placement: string; maxWidth?: string }>(
  ({ theme, maxWidth }) => ({
    pointerEvents: "auto",
    [`& .MuiTooltip-tooltip`]: {
      backgroundColor: theme.palette.background.tooltipSecondary,
      boxShadow: theme.shadows[1],
      border: `1px solid ${theme.palette.border.tooltipSecondary}`,
      maxWidth: maxWidth || "300px",
      pointerEvents: "auto",
    },
    [`& .MuiTooltip-arrow`]: {
      color: theme.palette.background.tooltipSecondary,
    },
    a: {
      color: theme.palette.text.onTooltipSecondaryLink,
    },
    zIndex: 10000,
  })
);

export type TooltipProps = {
  title?: string;
  text: ReactNode;
  children: JSX.Element;
  openOnClick?: boolean;
  isSmall?: boolean;
  variant?: "default" | "white";
  maxWidth?: string;
  isHovered?: boolean;
} & Omit<MuiTooltipProps, "title">;

export const Tooltip = ({
  title,
  text,
  openOnClick,
  isSmall,
  children,
  variant = "default",
  isHovered = false,
  ...props
}: TooltipProps) => {
  const theme = useTheme();
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(isHovered);
  const [prevIsHovered, setPrevIsHovered] = React.useState(isHovered);
  if (prevIsHovered !== isHovered) {
    setPrevIsHovered(isHovered);
    setIsTooltipOpen(isHovered);
  }
  const Child = useMemo(
    () =>
      openOnClick
        ? cloneElement(children, {
            onClick: (event: MouseEvent) => {
              event.stopPropagation();
              setIsTooltipOpen((prev) => !prev);
            },
          })
        : children,
    [children, openOnClick]
  );

  return (
    <StyledTooltip
      variant={variant}
      enterDelay={200}
      enterNextDelay={200}
      title={
        <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
          <Box py={isSmall ? 0 : 2} px={isSmall ? 0 : 1}>
            {title && (
              <Typography
                variant="body2"
                fontWeight={600}
                mb={2}
                color="text.onTooltipSecondary"
              >
                {title}
              </Typography>
            )}
            {typeof text === "string" ? (
              <Typography
                variant="body3"
                color={theme.palette.text.onTooltipSecondarySub}
              >
                {text}
              </Typography>
            ) : (
              text
            )}
          </Box>
        </ClickAwayListener>
      }
      placement="top"
      open={openOnClick ? isTooltipOpen : undefined}
      disableInteractive={false}
      {...props}
    >
      {Child}
    </StyledTooltip>
  );
};
